import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Container,
  Button,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import hospitalImage from '../../assets/homepageimages/hospital-image.webp';
import emergencyImage from '../../assets/homepageimages/emergency-service.webp';
import surgeryImage from '../../assets/homepageimages/surgery-clinic.webp';
import diagnosticImage from '../../assets/homepageimages/diagnostic-services.webp';
import { Link as RouterLink } from 'react-router-dom';
import HeroBackground, { preloadImage } from './HeroBackground';
import apiClient from '../../services/api';

// Fallback content: used until the CMS-backed slides load, and as a safety net if the
// /api/hero-slides request ever fails or returns nothing - the homepage must never break.
const DEFAULT_SLIDES = [
  {
    id: 1,
    image: hospitalImage,
    title: 'Welcome to Hearts International Hospital',
    subtitle: 'Providing exceptional healthcare with compassion and excellence',
    description:
      'Your health and well-being are our top priorities. Experience world-class medical care in a compassionate environment.',
    ctaText: 'Explore Our Services',
    ctaLink: '/services',
  },
  {
    id: 2,
    image: diagnosticImage,
    title: 'State-of-the-Art Medical Facilities',
    subtitle: 'Advanced technology meets compassionate care',
    description:
      'Our modern facilities are equipped with the latest medical technology to provide you with the best possible care.',
    ctaText: 'View Our Facilities',
    ctaLink: '/services',
  },
  {
    id: 3,
    image: surgeryImage,
    title: 'Expert Medical Team',
    subtitle: 'Dedicated healthcare professionals',
    description:
      'Our team of experienced doctors and healthcare professionals are committed to providing personalized care for every patient.',
    ctaText: 'Meet Our Doctors',
    ctaLink: '/doctors',
  },
  {
    id: 4,
    image: emergencyImage,
    title: '24/7 Emergency Care',
    subtitle: 'Always here when you need us',
    description:
      'Our emergency department is open 24/7, providing immediate medical attention when you need it most.',
    ctaText: 'Contact Us',
    ctaLink: '/contact',
  },
];

// The database only seeds slide text (see backend ensureHeroSlidesSchema), not image
// binaries, so the four default slides fall back to these local images until an admin
// uploads a real photo for them via /admin/home-content.
const DEFAULT_SLIDE_IMAGES = {
  hero_slide_1: hospitalImage,
  hero_slide_2: diagnosticImage,
  hero_slide_3: surgeryImage,
  hero_slide_4: emergencyImage,
};

const HeroSlider = () => {
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await apiClient.getHeroSlides();
        if (cancelled || !result || !result.success || !Array.isArray(result.data)) return;
        const active = result.data
          .filter((s) => s.isActive !== false)
          .map((s) => ({
            id: s.id,
            image: s.image || DEFAULT_SLIDE_IMAGES[s.id] || hospitalImage,
            title: s.title,
            subtitle: s.subtitle,
            description: s.description,
            ctaText: s.ctaText,
            ctaLink: s.ctaLink || '/services',
          }));
        if (active.length > 0) {
          setSlides(active);
          setCurrentSlide(0);
        }
      } catch (e) {
        // Network/API issue - keep showing DEFAULT_SLIDES, no user-facing error.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    preloadImage(slides[0].image);
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 20000);
    return () => clearInterval(timer);
  }, [slides]);

  useEffect(() => {
    const next = (currentSlide + 1) % slides.length;
    preloadImage(slides[next].image);
  }, [currentSlide, slides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const slide = slides[currentSlide];

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: '60vh', sm: '70vh', md: '75vh', lg: '80vh' },
        minHeight: { xs: 400, sm: 500, md: 550, lg: 600 },
        overflow: 'hidden',
      }}
    >
      <HeroBackground
        key={slide.id}
        src={slide.image}
        alt=""
        priority={currentSlide === 0}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              maxWidth: 800,
              color: 'white',
              textAlign: 'left',
              animation: 'slideIn 0.8s ease-out',
              '@keyframes slideIn': {
                '0%': { opacity: 0, transform: 'translateX(-50px)' },
                '100%': { opacity: 1, transform: 'translateX(0)' },
              },
            }}
          >
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: { xs: 1, sm: 1.5, md: 2, lg: 3 },
                textShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.5rem', lg: '3.5rem', xl: '4rem' },
                lineHeight: 1.2,
              }}
            >
              {slide.title}
            </Typography>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                mb: { xs: 1.5, sm: 2, md: 3, lg: 4 },
                opacity: 0.95,
                fontWeight: 400,
                textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.5rem', lg: '2rem', xl: '2.5rem' },
                lineHeight: 1.3,
              }}
            >
              {slide.subtitle}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: { xs: 2, sm: 3, md: 4, lg: 6 },
                opacity: 0.9,
                fontWeight: 300,
                textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem', lg: '1.25rem' },
                lineHeight: 1.5,
                maxWidth: 600,
              }}
            >
              {slide.description}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1.5, sm: 2, md: 3 },
                flexWrap: 'wrap',
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Button
                component={RouterLink}
                to={slide.ctaLink}
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: '#2563eb',
                  px: { xs: 2.5, sm: 3, md: 4 },
                  py: { xs: 1, sm: 1.5, md: 2 },
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.1rem' },
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#f8fafc',
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {slide.ctaText}
              </Button>
              <Button
                component={RouterLink}
                to="/contact"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: { xs: 2.5, sm: 3, md: 4 },
                  py: { xs: 1, sm: 1.5, md: 2 },
                  fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.1rem' },
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Get in Touch
              </Button>
            </Box>
          </Box>
        </Container>
      </HeroBackground>

      <IconButton
        onClick={prevSlide}
        sx={{
          position: 'absolute',
          left: { xs: 8, sm: 10, md: 20 },
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'rgba(255,255,255,0.2)',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
          zIndex: 3,
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: { xs: 20, sm: 24, md: 32 } }} />
      </IconButton>
      <IconButton
        onClick={nextSlide}
        sx={{
          position: 'absolute',
          right: { xs: 8, sm: 10, md: 20 },
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'rgba(255,255,255,0.2)',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
          zIndex: 3,
        }}
      >
        <ChevronRightIcon sx={{ fontSize: { xs: 20, sm: 24, md: 32 } }} />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 15, sm: 20, md: 30 },
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
          zIndex: 3,
        }}
      >
        {slides.map((_, index) => (
          <IconButton
            key={index}
            onClick={() => goToSlide(index)}
            sx={{
              p: 0.5,
              color: index === currentSlide ? 'white' : 'rgba(255,255,255,0.5)',
              '&:hover': { color: 'white' },
            }}
          >
            <CircleIcon
              sx={{
                fontSize:
                  index === currentSlide
                    ? { xs: 8, sm: 10, md: 12 }
                    : { xs: 5, sm: 6, md: 8 },
                transition: 'all 0.3s ease',
              }}
            />
          </IconButton>
        ))}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default HeroSlider;
